import _ from 'lodash';
import moment from 'moment';
import { useEffect, useMemo, useRef } from 'react';
import { handleMonitoringFrontendError } from '@/shared/api';
import { handleCallCenterActiveTimeTracking } from '@/entities/facility';
import { EMITTER_CONSTANTS, FRONT_END_ERRORS, KEY_RECOIL } from '@/shared/config';

import { useEmitter, useUpdateEffect } from '@/shared/lib/customHooks';
import { toastrSuccess } from '@/shared/lib/toastNotification';
import { useUserStore } from '@/entities/user';
import { selectActiveTab, useReportStore } from '@/entities/report';
import {
  BYPASS_APIS,
  CHECK_OVERFLOW_INTERVAL_DURATION,
  LIST_TRAKING_SPACE_ID,
  NO_API_CALLING_INTERVAL_DURATION,
  TIME_TRACKING_DURATION,
  TIME_TRACKING_EMITTER_CONSTANTS,
  TIMEOUT_DURATION,
  TRIGGERED_BY,
} from './timeTracking.c';
import {
  ActiveButton,
  checkFinishedInitializing,
  generateActiveButtonBeforeChanging,
  generateCurrentActiveButton,
  generateDetectedActions,
  generateTrackingType,
  shouldShowTrackingToast,
} from './timeTracking.h';

export type TimeTrackingProps = {
  activeTab: string;
  profileId: string;
  studyFid: string | number;
  reportType: string;
  reportId: string | number;
  displayNoApiCallingModal: () => void;
  isWsConnected: boolean;
  isLoadBeatData: boolean;
};

/**
 * Headless tracker that measures how long a reviewer actively works on each report tab and reports it to the call centre. Listens for mouse, key, scroll, click and visibility events to open and close timed windows, flushes a window whenever the tab, the sub-button, the initializing state or the AI-processing stop emitter changes, warns through monitoring when a window overruns, and raises the no-API-calling modal when no GraphQL operation was seen for a whole interval.
 *
 * @param props - Tracking props carrying activeTab, profileId, studyFid, reportType, reportId, displayNoApiCallingModal and the isWsConnected and isLoadBeatData start-up flags.
 * @returns Null, since the tracker renders no markup of its own.
 */
const TimeTracking = ({
  activeTab,
  profileId,
  studyFid,
  reportType,
  reportId,
  displayNoApiCallingModal,
  isWsConnected,
  isLoadBeatData,
}: TimeTrackingProps): null => {
  const meRedux = useUserStore((s) => s.me);

  const activeButtonBeats = useReportStore(selectActiveTab(KEY_RECOIL.BEAT_HR));
  const activeButtonEcgEvents = useReportStore(selectActiveTab(KEY_RECOIL.RHYTHM_EVENT));
  const activeButtonStripsManagement = useReportStore(selectActiveTab(KEY_RECOIL.STRIP_MANAGEMENT));
  const isStartedAiProcessing = useReportStore((s) => s.isStartedAiProcessing);
  const isLoadedIndexDB = useReportStore((s) => s.isLoadedIndexDB);
  const isCompletedDownloadBeat = useReportStore((s) => s.isCompletedDownloadBeat);

  const previousActiveButtonBeats = useRef<ActiveButton>(activeButtonBeats);
  const previousActiveButtonEcgEvents = useRef<ActiveButton>(activeButtonEcgEvents);
  const previousActiveButtonStripsManagement = useRef<ActiveButton>(activeButtonStripsManagement);
  const startTime = useRef<moment.Moment | null>(null);
  const timeTrackingTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const previousActiveTab = useRef(activeTab);
  const calledApis = useRef<any[]>([]);
  const mouseMoveCount = useRef(0);
  const untrustedMouseMoveCount = useRef(0);
  const keyDownCount = useRef(0);
  const untrustedKeyDownCount = useRef(0);
  const keyUpCount = useRef(0);
  const untrustedKeyUpCount = useRef(0);
  const scrollCount = useRef(0);
  const untrustedScrollCount = useRef(0);
  const clickedIds = useRef<string[]>([]);
  const untrustedClickedIds = useRef<string[]>([]);

  const initializingInfo = useMemo(
    () => ({
      isWsConnected,
      isLoadedIndexDB,
      isDownloadingBeats: isLoadBeatData || !isCompletedDownloadBeat,
      isInitializingStartAI: !isStartedAiProcessing,
    }),
    [isLoadBeatData, isLoadedIndexDB, isCompletedDownloadBeat, isStartedAiProcessing],
  );

  /**
   * Reports one slice of review time for the study to the call centre tracking API. Caps the reported active time at TIME_TRACKING_DURATION, posts it with the study, profile and report identifiers under the AIRP category, adds reportId only for Interim reports, shows a debug toast for allowed QC accounts, and raises a monitoring error when the raw duration exceeded the cap; API failures are only logged.
   *
   * @param dataLayer - The slice to report, carrying the activity type, raw duration in seconds, optional detectedActions summary and the triggerBy label used for the debug toast.
   * @returns A promise resolving once the tracking call and any monitoring report have finished or failed.
   */
  const handleSendData = async (dataLayer: {
    type: string;
    duration: number;
    detectedActions?: string;
    triggerBy: string;
  }) => {
    const activeTime =
      dataLayer.duration > TIME_TRACKING_DURATION ? TIME_TRACKING_DURATION : dataLayer.duration;
    const activityDetail = dataLayer.type;
    const input = {
      userId: meRedux.username || meRedux.id,
      tracking: [
        {
          studyFid,
          activityCategory: 'AIRP',
          activityDetail,
          profileId,
          reportType,
          activeTime,
          reportId: reportType === 'Interim' ? reportId : undefined,
          detectedActions: dataLayer.detectedActions || undefined,
        },
      ],
    };
    try {
      await handleCallCenterActiveTimeTracking(input);
      if (shouldShowTrackingToast(meRedux.id)) {
        console.log(`Time tracking was triggered by ${dataLayer.triggerBy}`);
        toastrSuccess(
          `Time tracking data sent successfully - Activity: ${activityDetail} - Active time: ${activeTime}s`,
        );
      }
      // Send data to monitoring if duration exceeds the limit
      if (dataLayer.duration > TIME_TRACKING_DURATION) {
        await handleMonitoringFrontendError({
          exception: JSON.stringify({
            name: 'TimeTrackingDurationError',
            message: 'Time tracking duration exceeded the limit',
            studyFid,
            profileId,
            reportType,
            reportId: reportType === 'Interim' ? reportId : undefined,
            activityCategory: 'AIRP',
            activityDetail: dataLayer.type,
            activeTime: dataLayer.duration,
          }),
          type: FRONT_END_ERRORS.TIME_TRACKING_DURATION_ERROR,
          userId: meRedux.id,
        });
      }
    } catch (error) {
      console.error('Failed to send time tracking data: ', error);
    }
  };

  /**
   * Ends the current active-time window without reporting it. Clears the recorded start moment and cancels the pending tracking timeout.
   *
   * @returns Nothing.
   */
  const handleResetTimeout = () => {
    startTime.current = null;
    clearTimeout(timeTrackingTimeout.current);
    timeTrackingTimeout.current = null;
  };

  /**
   * Discards the interaction counters collected for the current tracking window. Zeroes the mouse move, key up, scroll and key down tallies together with their untrusted counterparts.
   *
   * @returns Nothing.
   */
  const handleClearCountData = () => {
    mouseMoveCount.current = 0;
    untrustedMouseMoveCount.current = 0;
    keyUpCount.current = 0;
    untrustedKeyUpCount.current = 0;
    scrollCount.current = 0;
    untrustedScrollCount.current = 0;
    keyDownCount.current = 0;
    untrustedKeyDownCount.current = 0;
  };

  /**
   * Discards the click regions collected for the current tracking window. Empties both the clicked id list and the untrusted clicked id list.
   *
   * @returns Nothing.
   */
  const handleClearClickedData = () => {
    clickedIds.current = [];
    untrustedClickedIds.current = [];
  };

  /**
   * Counts pointer, key and scroll activity and opens a review time window on the first of them. Tallies mousemove, keyup and scroll events with a separate untrusted count while the tab is visible, and when no window is open records the start moment and arms a timeout that summarises the detected actions, resolves the tracking label for the current tab and sub-button, sends the slice, and clears the start moment.
   *
   * @param e - Mousemove, keyup or scroll event, whose type selects the counter and whose isTrusted flag marks synthetic activity.
   * @returns Nothing.
   */
  const handleMouseAndKey = (e: Event) => {
    if (document.visibilityState !== 'visible') {
      return;
    }

    const isMouseMoveEvent = e.type === 'mousemove';
    const isKeyUpEvent = e.type === 'keyup';
    const isScrollEvent = e.type === 'scroll';
    if (isMouseMoveEvent) {
      mouseMoveCount.current += 1;
      if (!e.isTrusted) {
        untrustedMouseMoveCount.current += 1;
      }
    } else if (isKeyUpEvent) {
      keyUpCount.current += 1;
      if (!e.isTrusted) {
        untrustedKeyUpCount.current += 1;
      }
    } else if (isScrollEvent) {
      scrollCount.current += 1;
      if (!e.isTrusted) {
        untrustedScrollCount.current += 1;
      }
    }

    if (!startTime.current) {
      startTime.current = moment();
      handleClearClickedData();

      timeTrackingTimeout.current = setTimeout(() => {
        const duration = TIME_TRACKING_DURATION;

        const detectedActions = generateDetectedActions({
          userId: meRedux.id,
          mouseMoveCount: mouseMoveCount.current,
          keyUpCount: keyUpCount.current,
          scrollCount: scrollCount.current,
          untrustedMouseMoveCount: untrustedMouseMoveCount.current,
          untrustedKeyUpCount: untrustedKeyUpCount.current,
          untrustedScrollCount: untrustedScrollCount.current,
          clickedIds: clickedIds.current,
          untrustedClickedIds: untrustedClickedIds.current,
          keyDownCount: keyDownCount.current,
          untrustedKeyDownCount: untrustedKeyDownCount.current,
        });
        handleClearCountData();
        handleClearClickedData();

        const currentActionButton = generateCurrentActiveButton({
          activeTab,
          activeButtonBeats,
          activeButtonEcgEvents,
          activeButtonStripsManagement,
        });
        const type = generateTrackingType(activeTab, currentActionButton, initializingInfo);
        const dataLayer = {
          type,
          duration,
          detectedActions,
          triggerBy: TRIGGERED_BY.MOUSE_AND_KEY_TIMEOUT,
        };
        handleSendData(dataLayer);

        startTime.current = null;
      }, TIMEOUT_DURATION);
    }
  };

  /**
   * Counts a key press towards the reviewer's detected activity. Increments the key down tally and, when the event was not user-generated, the untrusted key down tally as well.
   *
   * @param e - Keydown event, whose isTrusted flag decides if the untrusted tally is also raised.
   * @returns Nothing.
   */
  const handleKeyDown = (e: Event) => {
    keyDownCount.current += 1;
    if (!e.isTrusted) {
      untrustedKeyDownCount.current += 1;
    }
  };

  /**
   * Flushes the open review time window when the report tab is hidden. Ignores the event while the document stays visible, otherwise measures elapsed seconds, cancels the pending timeout, and when time actually elapsed summarises the interaction counters and clicked regions, resolves the tracking label for the current tab and sub-button and sends the slice tagged as a visibility change.
   *
   * @returns Nothing.
   */
  const handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      return;
    }

    const duration = startTime.current ? moment().unix() - startTime.current.unix() : 0;
    handleResetTimeout();

    if (duration <= 0) {
      handleClearCountData();
      handleClearClickedData();
      return;
    }

    const detectedActions = generateDetectedActions({
      userId: meRedux.id,
      mouseMoveCount: mouseMoveCount.current,
      keyUpCount: keyUpCount.current,
      scrollCount: scrollCount.current,
      untrustedMouseMoveCount: untrustedMouseMoveCount.current,
      untrustedKeyUpCount: untrustedKeyUpCount.current,
      untrustedScrollCount: untrustedScrollCount.current,
      clickedIds: clickedIds.current,
      untrustedClickedIds: untrustedClickedIds.current,
      keyDownCount: keyDownCount.current,
      untrustedKeyDownCount: untrustedKeyDownCount.current,
    });
    handleClearCountData();
    handleClearClickedData();

    const currentActionButton = generateCurrentActiveButton({
      activeTab,
      activeButtonBeats,
      activeButtonEcgEvents,
      activeButtonStripsManagement,
    });
    const type = generateTrackingType(activeTab, currentActionButton, initializingInfo);
    const dataLayer = {
      type,
      duration,
      detectedActions,
      triggerBy: TRIGGERED_BY.VISIBILITY_CHANGE,
    };
    handleSendData(dataLayer);
  };

  /**
   * Closes the review time window for the tab the user just left and opens a fresh one. Remembers the new tab, measures elapsed seconds since the window started, cancels the pending timeout, and when time actually elapsed summarises the interaction counters and clicked regions, resolves the previous tab's label from its own active sub-button, and sends the slice tagged as an active-tab change; a zero duration only clears the counters.
   *
   * @param changedActiveTab - Identifier of the tab now in view, stored as the new previous tab.
   * @returns Nothing.
   */
  const handleActiveTabChange = (changedActiveTab: string) => {
    const activeTabBeforeChanging = previousActiveTab.current;
    previousActiveTab.current = changedActiveTab;

    const duration = startTime.current ? moment().unix() - startTime.current.unix() : 0;
    handleResetTimeout();

    if (duration <= 0) {
      handleClearCountData();
      handleClearClickedData();
      return;
    }

    const detectedActions = generateDetectedActions({
      userId: meRedux.id,
      mouseMoveCount: mouseMoveCount.current,
      keyUpCount: keyUpCount.current,
      scrollCount: scrollCount.current,
      untrustedMouseMoveCount: untrustedMouseMoveCount.current,
      untrustedKeyUpCount: untrustedKeyUpCount.current,
      untrustedScrollCount: untrustedScrollCount.current,
      clickedIds: clickedIds.current,
      untrustedClickedIds: untrustedClickedIds.current,
      keyDownCount: keyDownCount.current,
      untrustedKeyDownCount: untrustedKeyDownCount.current,
    });
    handleClearCountData();
    handleClearClickedData();

    const activeButtonBeforeChagingActiveTab = generateCurrentActiveButton({
      activeTab: activeTabBeforeChanging,
      activeButtonBeats,
      activeButtonEcgEvents,
      activeButtonStripsManagement,
    });
    const type = generateTrackingType(
      activeTabBeforeChanging,
      activeButtonBeforeChagingActiveTab,
      initializingInfo,
    );
    const dataLayer = {
      type,
      duration,
      detectedActions,
      triggerBy: TRIGGERED_BY.ACTIVE_TAB_CHANGED,
    };
    handleSendData(dataLayer);
  };

  /**
   * Reports the time the reviewer spent waiting for the report to finish initializing. Measures elapsed seconds since the window started, cancels the pending timeout, and when time actually elapsed summarises the interaction counters and clicked regions and sends the slice under the Initializing activity; a zero duration only clears the counters.
   *
   * @returns Nothing.
   */
  const handleChangeInitializingStatus = () => {
    const duration = startTime.current ? moment().unix() - startTime.current.unix() : 0;
    handleResetTimeout();

    if (duration <= 0) {
      handleClearCountData();
      handleClearClickedData();
      return;
    }
    const detectedActions = generateDetectedActions({
      userId: meRedux.id,
      mouseMoveCount: mouseMoveCount.current,
      keyUpCount: keyUpCount.current,
      scrollCount: scrollCount.current,
      untrustedMouseMoveCount: untrustedMouseMoveCount.current,
      untrustedKeyUpCount: untrustedKeyUpCount.current,
      untrustedScrollCount: untrustedScrollCount.current,
      clickedIds: clickedIds.current,
      untrustedClickedIds: untrustedClickedIds.current,
      keyDownCount: keyDownCount.current,
      untrustedKeyDownCount: untrustedKeyDownCount.current,
    });
    handleClearCountData();
    handleClearClickedData();
    const dataLayer = {
      type: 'Initializing',
      duration,
      detectedActions,
      triggerBy: TRIGGERED_BY.ACTIVE_TAB_CHANGED,
    };
    handleSendData(dataLayer);
  };

  const handleActiveButtonChange = ({
    changedActiveButtonBeats,
    changedActiveButtonEcgEvents,
    changedActiveButtonStripsManagement,
  }: {
    changedActiveButtonBeats: ActiveButton;
    changedActiveButtonEcgEvents: ActiveButton;
    changedActiveButtonStripsManagement: ActiveButton;
  }) => {
    const activeButtonBeforeChanging = generateActiveButtonBeforeChanging({
      changedActiveButtonBeats,
      changedActiveButtonEcgEvents,
      changedActiveButtonStripsManagement,
      previousActiveButtonBeats: previousActiveButtonBeats.current,
      previousActiveButtonEcgEvents: previousActiveButtonEcgEvents.current,
      previousActiveButtonStripsManagement: previousActiveButtonStripsManagement.current,
    });
    previousActiveButtonBeats.current = changedActiveButtonBeats;
    previousActiveButtonEcgEvents.current = changedActiveButtonEcgEvents;
    previousActiveButtonStripsManagement.current = changedActiveButtonStripsManagement;

    const duration = startTime.current ? moment().unix() - startTime.current.unix() : 0;
    handleResetTimeout();

    if (duration <= 0) {
      handleClearCountData();
      handleClearClickedData();
      return;
    }

    const detectedActions = generateDetectedActions({
      userId: meRedux.id,
      mouseMoveCount: mouseMoveCount.current,
      keyUpCount: keyUpCount.current,
      scrollCount: scrollCount.current,
      untrustedMouseMoveCount: untrustedMouseMoveCount.current,
      untrustedKeyUpCount: untrustedKeyUpCount.current,
      untrustedScrollCount: untrustedScrollCount.current,
      clickedIds: clickedIds.current,
      untrustedClickedIds: untrustedClickedIds.current,
      keyDownCount: keyDownCount.current,
      untrustedKeyDownCount: untrustedKeyDownCount.current,
    });
    handleClearCountData();
    handleClearClickedData();

    const type = generateTrackingType(activeTab, activeButtonBeforeChanging, initializingInfo);
    const dataLayer = {
      type,
      duration,
      detectedActions,
      triggerBy: TRIGGERED_BY.ACTIVE_BUTTON_CHANGED,
    };
    handleSendData(dataLayer);
  };

  /**
   * Records which tracked region of the report the reviewer clicked. Walks LIST_TRAKING_SPACE_ID for the nearest matching ancestor of the click target, ignores clicks landing outside all of them, and pushes the region id onto the clicked list plus a separate untrusted list when the event was not user-generated.
   *
   * @param e - Click event whose target locates the tracked region and whose isTrusted flag marks synthetic clicks.
   * @returns Nothing.
   */
  const handleButtonClick = (e: MouseEvent) => {
    let definedClosestTag: Element | null | undefined;
    _.forEach(LIST_TRAKING_SPACE_ID, (id) => {
      const closestTag = (e.target as HTMLElement).closest(id);
      if (closestTag) {
        definedClosestTag = closestTag;
        return false;
      }
    });
    const clickedId = definedClosestTag?.id;

    if (!clickedId) {
      return;
    }

    clickedIds.current.push(clickedId);
    if (!e.isTrusted) {
      untrustedClickedIds.current.push(clickedId);
    }
  };

  useEffect(() => {
    // Check for overflow every 3 minutes
    const checkOverflowInterval = setInterval(() => {
      if (!startTime.current) {
        return;
      }

      const duration = moment().unix() - startTime.current.unix();
      handleResetTimeout();

      if (duration <= TIME_TRACKING_DURATION) {
        return;
      }

      // const detectedActions = generateDetectedActions({
      //   userId: meRedux.id,
      //   mouseMoveCount: mouseMoveCount.current,
      //   keyUpCount: keyUpCount.current,
      //   scrollCount: scrollCount.current,
      //   untrustedMouseMoveCount: untrustedMouseMoveCount.current,
      //   untrustedKeyUpCount: untrustedKeyUpCount.current,
      //   untrustedScrollCount: untrustedScrollCount.current,
      //   clickedIds: clickedIds.current,
      //   untrustedClickedIds: untrustedClickedIds.current,
      //   keyDownCount: keyDownCount.current,
      //   untrustedKeyDownCount: untrustedKeyDownCount.current,
      // });
      // handleClearCountData();
      // handleClearClickedData();

      try {
        const currentActionButton = generateCurrentActiveButton({
          activeTab,
          activeButtonBeats,
          activeButtonEcgEvents,
          activeButtonStripsManagement,
        });
        const type = generateTrackingType(activeTab, currentActionButton, initializingInfo);
        // const dataLayer = {
        //   type,
        //   duration,
        //   detectedActions,
        // };
        // handleSendData(dataLayer);
        handleMonitoringFrontendError({
          exception: JSON.stringify({
            name: 'TimeoutNotTriggeredError',
            message: 'Timeout exceeded the limit but not triggered',
            studyFid,
            profileId,
            reportType,
            reportId: reportType === 'Interim' ? reportId : undefined,
            activityCategory: 'AIRP',
            activityDetail: type,
            activeTime: duration,
          }),
          type: FRONT_END_ERRORS.TIMEOUT_NOT_TRIGGERED_ERROR,
          userId: meRedux.id,
        });
      } catch (error) {
        console.error('Failed to send time tracking data: ', error);
      }
    }, CHECK_OVERFLOW_INTERVAL_DURATION);
    return () => {
      clearInterval(checkOverflowInterval);
    };
  }, [meRedux.id, activeTab]);

  useEffect(() => {
    // Check for no API calling every 15 minutes
    const noApiCallingInterval = setInterval(() => {
      if (calledApis.current.length === 0) {
        displayNoApiCallingModal();
      } else {
        calledApis.current = [];
      }
    }, NO_API_CALLING_INTERVAL_DURATION);
    return () => {
      clearInterval(noApiCallingInterval);
    };
  }, [meRedux.id, activeTab]);

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseAndKey);
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleMouseAndKey);
    window.addEventListener('scroll', handleMouseAndKey);
    window.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('mousemove', handleMouseAndKey);
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleMouseAndKey);
      window.removeEventListener('scroll', handleMouseAndKey);
      window.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [
    meRedux.id,
    activeTab,
    activeButtonBeats,
    activeButtonEcgEvents,
    activeButtonStripsManagement,
    initializingInfo,
  ]);

  useEffect(
    () => () => {
      handleResetTimeout();
      handleClearClickedData();
    },
    [],
  );

  useEffect(() => {
    document.addEventListener('click', handleButtonClick);
    return () => {
      document.removeEventListener('click', handleButtonClick);
    };
  }, []);

  useUpdateEffect(() => {
    handleActiveTabChange(activeTab);
  }, [activeTab]);

  useUpdateEffect(() => {
    // First time changing isInitializing (false) => log time tracking data
    if (checkFinishedInitializing(initializingInfo, activeTab)) {
      handleChangeInitializingStatus();
    }
  }, [initializingInfo]);

  useUpdateEffect(() => {
    handleActiveButtonChange({
      changedActiveButtonBeats: activeButtonBeats,
      changedActiveButtonEcgEvents: activeButtonEcgEvents,
      changedActiveButtonStripsManagement: activeButtonStripsManagement,
    });
  }, [activeButtonBeats, activeButtonEcgEvents, activeButtonStripsManagement]);

  useEmitter(
    EMITTER_CONSTANTS.STOP_AI_PROCESSING,
    () => {
      const duration = startTime.current ? moment().unix() - startTime.current.unix() : 0;
      handleResetTimeout();

      if (duration <= 0) {
        handleClearCountData();
        handleClearClickedData();
        return;
      }

      const detectedActions = generateDetectedActions({
        userId: meRedux.id,
        mouseMoveCount: mouseMoveCount.current,
        keyUpCount: keyUpCount.current,
        scrollCount: scrollCount.current,
        untrustedMouseMoveCount: untrustedMouseMoveCount.current,
        untrustedKeyUpCount: untrustedKeyUpCount.current,
        untrustedScrollCount: untrustedScrollCount.current,
        clickedIds: clickedIds.current,
        untrustedClickedIds: untrustedClickedIds.current,
        keyDownCount: keyDownCount.current,
        untrustedKeyDownCount: untrustedKeyDownCount.current,
      });
      handleClearCountData();
      handleClearClickedData();

      const currentActiveButton = generateCurrentActiveButton({
        activeTab,
        activeButtonBeats,
        activeButtonEcgEvents,
        activeButtonStripsManagement,
      });
      const type = generateTrackingType(activeTab, currentActiveButton, initializingInfo);
      const dataLayer = {
        type,
        duration,
        detectedActions,
        triggerBy: TRIGGERED_BY.STOP_AI_PROCESSING_EMITTER,
      };
      handleSendData(dataLayer);
    },
    [
      meRedux.id,
      activeTab,
      activeButtonBeats,
      activeButtonEcgEvents,
      activeButtonStripsManagement,
      initializingInfo,
    ],
  );

  useEmitter(
    TIME_TRACKING_EMITTER_CONSTANTS.API_TRACKING,
    (operation: { operationName: string }) => {
      if (!_.isEmpty(operation)) {
        if (!BYPASS_APIS.includes(operation.operationName)) {
          calledApis.current.push(operation);
        }
      }
    },
    [],
  );

  return null;
};

export default TimeTracking;
